import { useTranslation } from "react-i18next";
import { GradingInput } from "./GradingInput";
import { GRADE_TYPE_CONDITION, GRADE_TYPE_MANAGEMENT } from "../../utils/GradeUtils";
import "./GradingSectionTable.css";

/**
 * One grading row. `weight` is displayed read-only; `uiGrade` is the editable
 * grade on the 0–100 UI scale (null when ungraded). `type` groups the row into
 * the Condition or Management block.
 */
export interface GradingSectionRow {
    vesselGradeId: number;
    name: string;
    weight: number;
    type: number;
    uiGrade: number | null;
}

export interface GradingSectionTableProps {
    sections: GradingSectionRow[];
    /** Called when a row's grade changes; value is snapped to a multiple of 10
     *  (or null when cleared). */
    onGradeChange: (vesselGradeId: number, value: number | null) => void;
    /** When true, every grade renders as a read-only lozenge (e.g. Full Report). */
    readOnly?: boolean;
}

export const GradingSectionTable = ({ sections, onGradeChange, readOnly }: GradingSectionTableProps) => {
    const { t } = useTranslation("locale");

    if (sections.length === 0) {
        return (
            <div className="flex align-items-center justify-content-center m-6">
                <span className="font-semibold">{t("grading.noSections")}</span>
            </div>
        );
    }

    const columnHeaders = (key: string, className = "") => (
        <div key={key} className={`col-12 lg:col-6 grading-col-headers ${className}`.trim()}>
            <div className="flex align-items-center">
                <span className="small-text grading-cell-name">{t("grading.table.section")}</span>
                <span className="small-text grading-cell-weight">{t("grading.table.weighting")}</span>
                <span className="small-text grading-cell-grade">{t("grading.table.grade")}</span>
            </div>
        </div>
    );

    const entry = (row: GradingSectionRow) => (
        <div key={row.vesselGradeId} className="col-12 lg:col-6 grade-entry">
            <div className="flex align-items-center grading-row">
                <span className="grade-name grading-cell-name">{row.name}</span>
                {/* Weighting is display-only — never an editor. */}
                <span className="grading-cell-weight grading-weight">{row.weight.toFixed(2)}</span>
                <div className="grading-cell-grade">
                    <GradingInput
                        inputId={`grade-${row.vesselGradeId}`}
                        value={row.uiGrade}
                        onChange={(value) => onGradeChange(row.vesselGradeId, value)}
                        readOnly={readOnly}
                    />
                </div>
            </div>
        </div>
    );

    const conditionSections = sections.filter((section) => section.type === GRADE_TYPE_CONDITION);
    const managementSections = sections.filter((section) => section.type === GRADE_TYPE_MANAGEMENT);

    return (
        <div className="grading-sections" data-testid="grading-sections">
            <div className="flex flex-wrap grading-headers">
                {columnHeaders("header-left")}
                {/* The right-hand column disappears below lg (entries stack to
                    one per row), so its header must go with it. */}
                {columnHeaders("header-right", "hidden lg:block")}
            </div>
            <div className="flex flex-wrap">{conditionSections.map(entry)}</div>
            {managementSections.length > 0 && (
                <div className="flex flex-wrap mt-3">{managementSections.map(entry)}</div>
            )}
        </div>
    );
};
